import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import Paywall from "@/components/Paywall";

const formatWhen = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "en" ? "en-US" : "es-CO", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const MyBookings = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const r = t.redApoyo;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/my-bookings");
      setItems(data.items || []);
    } catch (err) {
      console.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm(r.cancelBooking + "?")) return;
    try { await api.post(`/appointments/${id}/cancel`); await load(); }
    catch (err) { window.alert(formatApiError(err)); }
  };

  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (user === false) return <Paywall />;

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight max-w-3xl">
        <Link to="/red-apoyo" className="inline-flex items-center gap-2 text-sm text-teal-soft hover:text-orange mb-6" data-testid="back-to-redapoyo">
          <ArrowLeft size={14} /> RED Online
        </Link>
        <p className="eyebrow text-orange">{r.navLabel}</p>
        <h1 className="font-display text-4xl md:text-5xl text-teal-deep mt-2 leading-tight">{r.myBookingsTitle}</h1>

        <div className="mt-10 space-y-4">
          {loading ? (
            <p className="text-teal-soft text-sm">…</p>
          ) : items.length === 0 ? (
            <p className="text-teal-soft text-sm italic" data-testid="my-bookings-empty">{r.myBookingsEmpty}</p>
          ) : items.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-4" data-testid={`booking-${s.id}`}>
              <span className="w-12 h-12 rounded-full bg-orange/15 text-orange flex items-center justify-center shrink-0">
                <Calendar size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl text-teal-deep leading-tight">{s.professional_business || s.professional_name}</h3>
                <p className="text-sm text-teal-soft mt-1">"{s.topic}"</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-teal-soft flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatWhen(s.start_at, lang)}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.duration_min} min</span>
                </div>
              </div>
              <button type="button" onClick={() => cancel(s.id)} className="text-red-500 hover:text-red-700 text-sm inline-flex items-center gap-1" data-testid={`cancel-${s.id}`}>
                <Trash2 size={13} /> {r.cancelBooking}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MyBookings;
